import {useSharedFilter} from '../../../shared/navigation/useSharedReport';
import { TableSearchScope, SectionTableSearch } from "../../../shared/ui/table/TableSearchScope";
// src/features/boe/components/ProgramBudgetByYear.jsx

import React, { useEffect, useMemo, useRef, useState } from "react";
import { getDoc, doc } from "firebase/firestore";
import { db } from "../../../firebase";
import { CommonTable } from "../../../shared/ui/table/CommonTable.jsx";
import { HeaderToggleButton } from "../../../shared/ui/overlay/HeaderToggleButton.jsx";
import { formatFiscalYearFromStart } from "../../../shared/utils/fiscalYearUtils.js";
import {
  LineChartWithToggles,
  BudgetInflationChart,
} from "../../../shared/ui/chart/LineChartWithToggles.jsx";
import { buildInflationChartData } from "../../../shared/ui/chart/charts/programBudgetCharts.js";
import { PieChartWithToggles } from "../../../shared/ui/chart/PieChartWithToggles.jsx";
import { PROGRAM_CONFIG } from "../../../shared/constants/programBudget.js";
import {
  toYearNumber,
  sumSectionTotals,
  getApprovedTotalWithProposedFallback,
  getDocIdFromProgramKey,
  buildYearAvailability,
  getValidWindowEndingYears,
  isProposedOnlyMode,
  build4ColWindowMeta,
} from "../../../budget/utils/programBudgetUtils.js";

/**
 * Collection where the per-program docs live (e.g., "0200", "0300", "0400", etc.).
 * @type {string}
 */
const BOARD_OF_ED_PROGRAMS_COLLECTION = "publicSchools";

/**
 * Attaches "Change" and "% Change" fields based on the last two visible
 * columns described by metaList.
 *
 * Notes:
 * - Mutates the `row` object in-place (matches current usage).
 * - If prevVal is 0, percentIncrease becomes null to avoid division by zero.
 * - If either value is not numeric, leaves fields unset (keeps existing behavior).
 *
 * @param {Object} row - Row object being built for CommonTable.
 * @param {{colKey:string}[]} metaList - Column metadata list (uses last two entries).
 * @returns {Object} The same row reference.
 */
function attachIncreaseFields(row, metaList) {
  if (metaList.length < 2) return row;

  const prevMeta = metaList[metaList.length - 2];
  const currMeta = metaList[metaList.length - 1];

  const prevVal = row[prevMeta.colKey];
  const currVal = row[currMeta.colKey];

  if (typeof prevVal === "number" && typeof currVal === "number") {
    const diff = currVal - prevVal;
    row.increase = diff;
    row.percentIncrease = prevVal !== 0 ? diff / prevVal : null;
  }

  return row;
}

/**
 * Matches the header style in ProgramBudget tables:
 * - Fiscal Year label on the first line
 * - Section label on the second line (Approved/Actual/Proposed)
 *
 * @param {string} fyLabel - Fiscal year label.
 * @param {string} sectionLabel - Section label.
 * @returns {JSX.Element} Two-line header element.
 */
function buildTwoLineHeader(fyLabel, sectionLabel) {
  return (
    <div className="leading-tight text-center whitespace-nowrap">
      <div className="font-semibold">{fyLabel}</div>
      <div>{sectionLabel}</div>
    </div>
  );
}

/**
 * ProgramBudgetByYear
 *
 * Renders:
 * - A 4-column snapshot table of per-program totals (window depends on Proposed-mode rules)
 * - A "Total Budget" row summing all visible programs
 * - Optional charts:
 *   1) Approved totals over time (sum across programs)
 *   2) Actual totals over time (sum across programs)
 *   3) Actual Spending vs Approved Budget comparison (common years only)
 *   4) Program share pie for a selected year and budget type (Approved/Actual/Proposed)
 *
 * Data sources:
 * - Firestore per-program docs in BOARD_OF_ED_PROGRAMS_COLLECTION (doc ids derived from PROGRAM_CONFIG)
 *
 * @param {Object} props
 * @param {boolean} [props.isEditing=false] - Present for API consistency; table is rendered read-only here.
 * @param {Function} props.setBoeData - Optional setter to bubble up computed table rows.
 * @returns {JSX.Element}
 */
export function ProgramBudgetByYear({ isEditing = false, setBoeData }) {
  const excelExportRef = useRef(null);

  const [programDocs, setProgramDocs] = useState({});
  const [yearNumbers, setYearNumbers] = useState([]);
  const [inflationByYear, setInflationByYear] = useState({});
  const [showInflationTotalsChart, setShowInflationTotalsChart] =
    useState(false);
  const [endingYear, setEndingYear] = useSharedFilter("ProgramBudgetByYear.jsx:ProgramBudgetByYear:endingYear", null);
  const [loading, setLoading] = useState(true);

  const [showProgramsTable, setShowProgramsTable] = useState(true);

  const [showApprovedTotalsChart, setShowApprovedTotalsChart] = useState(false);
  const [showActualTotalsChart, setShowActualTotalsChart] = useState(false);
  const [showDiffTotalsChart, setShowDiffTotalsChart] = useState(false);
  const [showProgramSharePieChart, setShowProgramSharePieChart] =
    useState(false);

  const [pieYear, setPieYear] = useSharedFilter("ProgramBudgetByYear.jsx:ProgramBudgetByYear:pieYear", null); // selected fiscal start year (e.g., 2013)
  const [pieBudgetType, setPieBudgetType] = useSharedFilter("ProgramBudgetByYear.jsx:ProgramBudgetByYear:pieBudgetType", "Approved"); // "Approved" | "Actual" | "Proposed"

  /**
   * Loads all program docs once on mount.
   * Uses an `alive` flag to avoid updating state after unmount.
   */
  useEffect(() => {
    let alive = true;

    async function loadAllPrograms() {
      setLoading(true);
      try {
        const docIds = PROGRAM_CONFIG.map((p) => getDocIdFromProgramKey(p.key));

        const [inflSnap, ...snaps] = await Promise.all([
          getDoc(doc(db, "common", "inflationByYear")),
          ...docIds.map((id) =>
            getDoc(doc(db, BOARD_OF_ED_PROGRAMS_COLLECTION, id)),
          ),
        ]);

        if (!alive) return;

        const docsById = {};
        const yearSet = new Set();

        snaps.forEach((snap, idx) => {
          const id = docIds[idx];

          if (!snap.exists()) {
            console.warn(
              `Program doc ${BOARD_OF_ED_PROGRAMS_COLLECTION}/${id} not found`,
            );
            return;
          }

          const data = snap.data();
          docsById[id] = data;

          Object.keys(data || {}).forEach((key) => {
            const y = toYearNumber(key);
            if (y) yearSet.add(y);
          });
        });

        const allYears = Array.from(yearSet).sort((a, b) => a - b);

        setProgramDocs(docsById);
        setYearNumbers(allYears);

        // Inflation
        if (inflSnap.exists()) {
          const inflData = inflSnap.data();
          setInflationByYear(inflData?.years || {});
        } else {
          setInflationByYear({});
        }
      } catch (err) {
        console.error("Error loading program docs:", err);
        if (alive) {
          setProgramDocs({});
          setYearNumbers([]);
          setEndingYear.initialize(null);
          setInflationByYear({});
        }
      } finally {
        if (alive) setLoading(false);
      }
    }

    loadAllPrograms();
    return () => {
      alive = false;
    };
  }, [setBoeData]);

  // ---------- Availability / Ending year options ------------------------

  /**
   * Availability matrix across years for Actual/Approved/Proposed.
   */
  const yearAvail = useMemo(
    () => buildYearAvailability(programDocs),
    [programDocs],
  );

  /**
   * Pie chart years: any year with at least one available section (Actual/Approved/Proposed).
   */
  const pieYearOptions = useMemo(() => {
    if (!yearNumbers.length) return [];
    return yearNumbers
      .filter((y) => {
        const slot = yearAvail.get(y);
        return (
          slot?.hasActual === true ||
          slot?.hasApproved === true ||
          slot?.hasProposed === true
        );
      })
      .sort((a, b) => a - b);
  }, [yearNumbers, yearAvail]);

  /**
   * Pie budget types available for the selected pieYear (only show types that exist).
   */
  const availablePieTypes = useMemo(() => {
    if (!pieYear) return [];
    const slot = yearAvail.get(pieYear);
    const types = [];
    if (slot?.hasApproved) types.push("Approved");
    if (slot?.hasActual) types.push("Actual");
    if (slot?.hasProposed) types.push("Proposed");
    return types;
  }, [pieYear, yearAvail]);

  /**
   * Ensures pieBudgetType is always valid for the selected pieYear.
   * Preference order: Approved -> Actual -> Proposed
   */
  useEffect(() => {
    if (!availablePieTypes.length) return;

    setPieBudgetType.initialize((prev) => {
      if (availablePieTypes.includes(prev)) return prev;
      if (availablePieTypes.includes("Approved")) return "Approved";
      if (availablePieTypes.includes("Actual")) return "Actual";
      return "Proposed";
    });
  }, [availablePieTypes]);

  /**
   * Pie items for program share:
   * - One item per program with a finite, non-zero total for pieYear+pieBudgetType.
   */
  const programSharePieItems = useMemo(() => {
    if (!pieYear || !pieBudgetType) return [];
    if (!Object.keys(programDocs).length) return [];

    const yearKey = String(pieYear);

    const items = PROGRAM_CONFIG.map((cfg) => {
      const docId = getDocIdFromProgramKey(cfg.key);
      const progDoc = programDocs[docId];
      if (!progDoc) return null;

      const section = progDoc?.[yearKey]?.[pieBudgetType];
      const total = sumSectionTotals(section);

      if (typeof total !== "number" || !Number.isFinite(total) || total === 0) {
        return null;
      }

      return {
        key: docId,
        label: cfg.label,
        value: total,
        valueType: "currency",
        group: "other",
      };
    }).filter(Boolean);

    return items;
  }, [pieYear, pieBudgetType, programDocs]);

  /**
   * Default pie year:
   * - Prefer endingYear (if available in pieYearOptions)
   * - Otherwise use the latest available year
   */
  useEffect(() => {
    if (!pieYearOptions.length) return;

    setPieYear.initialize((prev) => {
      const preferred =
        endingYear && pieYearOptions.includes(endingYear)
          ? endingYear
          : pieYearOptions[pieYearOptions.length - 1];

      return prev && pieYearOptions.includes(prev) ? prev : preferred;
    });
  }, [pieYearOptions, endingYear]);

  /**
   * Ending Year options per snapshot rules.
   */
  const endingYearOptions = useMemo(() => {
    if (!yearNumbers.length) return [];
    return getValidWindowEndingYears(yearNumbers, yearAvail);
  }, [yearNumbers, yearAvail]);

  // ---- Approved totals (approved-only) for diff chart intersections ----
  const approvedOnlyTotalsPoints = useMemo(() => {
    if (!Object.keys(programDocs).length || !yearNumbers.length) return [];

    const years = yearNumbers.filter((y) => yearAvail.get(y)?.hasApproved);

    return years
      .map((y) => {
        let sum = 0;
        let sawAny = false;

        Object.values(programDocs).forEach((docData) => {
          const section = docData?.[String(y)]?.Approved;
          const total = sumSectionTotals(section);

          if (typeof total === "number" && Number.isFinite(total)) {
            sum += total;
            sawAny = true;
          }
        });

        return sawAny ? { year: y, total: sum } : null;
      })
      .filter(Boolean);
  }, [programDocs, yearNumbers, yearAvail]);

  // ---- Approved timeline with proposed fallback (for Approved chart) ----
  const approvedTotalsTimelinePoints = useMemo(() => {
    if (!Object.keys(programDocs).length || !yearNumbers.length) return [];

    const years = yearNumbers.filter(
      (y) => yearAvail.get(y)?.hasApproved || yearAvail.get(y)?.hasProposed,
    );

    return years
      .map((y) => {
        let approvedSum = 0;
        let proposedSum = 0;
        let sawApproved = false;
        let sawProposed = false;

        Object.values(programDocs).forEach((docData) => {
          const yearObj = docData?.[String(y)];

          const a = sumSectionTotals(yearObj?.Approved);
          if (typeof a === "number" && Number.isFinite(a)) {
            approvedSum += a;
            sawApproved = true;
          }

          const p = sumSectionTotals(yearObj?.Proposed);
          if (typeof p === "number" && Number.isFinite(p)) {
            proposedSum += p;
            sawProposed = true;
          }
        });

        if (!sawApproved && !sawProposed) return null;

        return {
          year: y,
          approved: sawApproved ? approvedSum : null,
          proposed: sawProposed ? proposedSum : null,
        };
      })
      .filter(Boolean);
  }, [programDocs, yearNumbers, yearAvail]);

  /**
   * Actual totals points by year (sum across programs).
   */
  const actualTotalsPoints = useMemo(() => {
    if (!Object.keys(programDocs).length || !yearNumbers.length) return [];

    const years = yearNumbers.filter((y) => yearAvail.get(y)?.hasActual);

    return years
      .map((y) => {
        let sum = 0;
        let sawAny = false;

        Object.values(programDocs).forEach((docData) => {
          const section = docData?.[String(y)]?.Actual;
          const total = sumSectionTotals(section);
          if (typeof total === "number" && Number.isFinite(total)) {
            sum += total;
            sawAny = true;
          }
        });

        return sawAny ? { year: y, total: sum } : null;
      })
      .filter(Boolean);
  }, [programDocs, yearNumbers, yearAvail]);

  /**
   * Line chart model for total approved budget by year.
   */
  const approvedTotalsChart = useMemo(() => {
    if (!approvedTotalsTimelinePoints.length) return { labels: [], series: [] };

    const labels = approvedTotalsTimelinePoints.map((p) =>
      formatFiscalYearFromStart(p.year),
    );

    const approvedData = approvedTotalsTimelinePoints.map((p) => p.approved);

    const proposedOnlyData = approvedTotalsTimelinePoints.map((p) =>
      p.approved == null && typeof p.proposed === "number" ? p.proposed : null,
    );

    return {
      labels,
      series: [
        {
          key: "totalApproved",
          label: "Total Approved Budget",
          data: approvedData,
          proposedData: proposedOnlyData,
          group: "budget",
          defaultVisible: true,
          valueType: "currency",
        },
      ],
    };
  }, [approvedTotalsTimelinePoints]);

  /**
   * Line chart model for total actual spending by year.
   */
  const actualTotalsChart = useMemo(() => {
    if (!actualTotalsPoints.length) return { labels: [], series: [] };

    const labels = actualTotalsPoints.map((p) =>
      formatFiscalYearFromStart(p.year),
    );

    const data = actualTotalsPoints.map((p) => p.total);

    return {
      labels,
      series: [
        {
          key: "totalActual",
          label: "Total Actual Spending",
          data,
          group: "spending",
          defaultVisible: true,
          valueType: "currency",
        },
      ],
    };
  }, [actualTotalsPoints]);

  /**
   * Comparison chart (Actual Spending vs Approved Budget) for years where both are present.
   */
  const diffTotalsChart = useMemo(() => {
    if (!approvedOnlyTotalsPoints.length || !actualTotalsPoints.length) {
      return { labels: [], series: [], groups: [] };
    }

    const approvedByYear = new Map(
      approvedOnlyTotalsPoints.map((p) => [p.year, p.total]),
    );
    const actualByYear = new Map(
      actualTotalsPoints.map((p) => [p.year, p.total]),
    );

    const commonYears = [...approvedByYear.keys()]
      .filter((y) => actualByYear.has(y))
      .sort((a, b) => a - b);

    if (!commonYears.length) return { labels: [], series: [], groups: [] };

    const labels = commonYears.map((y) => formatFiscalYearFromStart(y));

    const series = [
      {
        key: "totalApproved",
        label: "Total Approved Budget",
        data: commonYears.map((y) => approvedByYear.get(y) ?? 0),
        group: "budget",
        defaultVisible: true,
        valueType: "currency",
      },
      {
        key: "totalActual",
        label: "Total Actual Spending",
        data: commonYears.map((y) => actualByYear.get(y) ?? 0),
        group: "spending",
        defaultVisible: true,
        valueType: "currency",
      },
    ];

    const groups = [
      {
        key: "totalBudget",
        label: "Total Budget",
        groupKey: "budget",
        seriesKeys: ["totalApproved", "totalActual"],
        defaultVisible: true,
      },
    ];

    return { labels, series, groups };
  }, [approvedOnlyTotalsPoints, actualTotalsPoints]);

  // ---------- Budget vs Inflation (Totals) -------------------------------

  // Minimal “fieldDefs” compatible with buildInflationChartData
  const TOTAL_BUDGET_FIELD_DEFS = useMemo(
    () => [
      {
        key: "totalBudget",
        label: "Total Budget",
        valueType: "currency",
        group: "budget",
        defaultVisible: true,
        sources: [],
      },
    ],
    [],
  );

  // Convert timeline points into “row arrays” shaped like ProgramBudget rows
  const approvedTotalsRowsForInflation = useMemo(() => {
    return (approvedTotalsTimelinePoints || [])
      .map((p) => {
        if (typeof p?.approved !== "number" || !Number.isFinite(p.approved)) {
          return null;
        }
        return { year: p.year, label: "Total", totalBudget: p.approved };
      })
      .filter(Boolean);
  }, [approvedTotalsTimelinePoints]);

  const proposedTotalsRowsForInflation = useMemo(() => {
    return (approvedTotalsTimelinePoints || [])
      .map((p) => {
        if (typeof p?.proposed !== "number" || !Number.isFinite(p.proposed)) {
          return null;
        }
        return { year: p.year, label: "Total", totalBudget: p.proposed };
      })
      .filter(Boolean);
  }, [approvedTotalsTimelinePoints]);

  const actualTotalsRowsForInflation = useMemo(() => {
    return (actualTotalsPoints || [])
      .map((p) => {
        if (typeof p?.total !== "number" || !Number.isFinite(p.total)) {
          return null;
        }
        return { year: p.year, label: "Total", totalBudget: p.total };
      })
      .filter(Boolean);
  }, [actualTotalsPoints]);

  const {
    inflationLabels,
    approvedInflationSeries,
    actualInflationSeries,
    inflationBaselineSeries,
    inflationBaselineSeriesForActual,
  } = useMemo(() => {
    return buildInflationChartData(
      approvedTotalsRowsForInflation,
      actualTotalsRowsForInflation,
      proposedTotalsRowsForInflation,
      inflationByYear,
      TOTAL_BUDGET_FIELD_DEFS,
      ["totalBudget"],
    );
  }, [
    approvedTotalsRowsForInflation,
    actualTotalsRowsForInflation,
    proposedTotalsRowsForInflation,
    inflationByYear,
    TOTAL_BUDGET_FIELD_DEFS,
  ]);

  /**
   * Default ending year: latest valid option.
   */
  useEffect(() => {
    if (!endingYearOptions.length) return;
    setEndingYear.initialize((prev) =>
      prev && endingYearOptions.includes(prev)
        ? prev
        : endingYearOptions[endingYearOptions.length - 1],
    );
  }, [endingYearOptions]);

  /**
   * Proposed-only mode: Proposed exists and Approved does not for endingYear.
   */
  const isProposedMode = useMemo(() => {
    if (!endingYear) return false;
    return isProposedOnlyMode(endingYear, yearAvail);
  }, [endingYear, yearAvail]);

  /**
   * 4-column snapshot window definition.
   */
  const windowYearsMeta = useMemo(() => {
    return build4ColWindowMeta(endingYear, isProposedMode);
  }, [endingYear, isProposedMode]);

  /**
   * Snapshot table columns.
   */
  const tableColumns = useMemo(() => {
    if (!windowYearsMeta.length) return [];

    const LABEL_COL_WIDTH = "320px";
    const YEAR_COL_WIDTH = "140px";
    const DELTA_COL_WIDTH = "100px";

    const cols = [
      {
        key: "label",
        label: "",
        style: { width: LABEL_COL_WIDTH, minWidth: LABEL_COL_WIDTH },
        editable: false,
      },
    ];

    const proposedColKey = isProposedMode
      ? windowYearsMeta.find((m) => m.sectionKey === "Proposed")?.colKey
      : null;

    windowYearsMeta.forEach(({ year, sectionKey, colKey }) => {
      const fy = formatFiscalYearFromStart(year);

      const sectionLabel =
        sectionKey === "Approved"
          ? "Approved"
          : sectionKey === "Proposed"
            ? "Proposed"
            : "Actual";

      const isHighlighted = proposedColKey && colKey === proposedColKey;

      cols.push({
        key: colKey,
        label: buildTwoLineHeader(fy, sectionLabel),
        format: "currency",
        align: "right",
        style: { width: YEAR_COL_WIDTH, minWidth: YEAR_COL_WIDTH },
        headerClassName: isHighlighted ? "bg-yellow-100" : undefined,
        cellClassName: isHighlighted ? "bg-yellow-50" : undefined,
      });
    });

    cols.push(
      {
        key: "increase",
        label: "Change",
        format: "currency",
        align: "right",
        style: { width: DELTA_COL_WIDTH, minWidth: DELTA_COL_WIDTH },
      },
      {
        key: "percentIncrease",
        label: "% Change",
        format: "percent",
        align: "right",
        style: { width: DELTA_COL_WIDTH, minWidth: DELTA_COL_WIDTH },
      },
    );

    return cols;
  }, [windowYearsMeta, isProposedMode]);

  /**
   * Builds one row per program for the snapshot window.
   */
  const programTableRows = useMemo(() => {
    if (!Object.keys(programDocs).length || !windowYearsMeta.length) return [];

    const rows = [];

    PROGRAM_CONFIG.forEach((cfg) => {
      const docId = getDocIdFromProgramKey(cfg.key);
      const progDoc = programDocs[docId];
      if (!progDoc) return;

      const row = { label: cfg.label };
      let hasAny = false;

      windowYearsMeta.forEach(({ year, sectionKey, colKey }) => {
        const yearKey = String(year);
        const yearObj = progDoc?.[yearKey];

        let total = null;

        const shouldApprovedFallback =
          !isProposedMode && sectionKey === "Approved" && year === endingYear;

        if (shouldApprovedFallback) {
          total = getApprovedTotalWithProposedFallback(yearObj);
        } else {
          const section = yearObj?.[sectionKey];
          total = sumSectionTotals(section);
        }

        if (typeof total === "number" && Number.isFinite(total)) {
          row[colKey] = total;
          hasAny = true;
        }
      });

      if (!hasAny) return;

      attachIncreaseFields(row, windowYearsMeta);
      rows.push(row);
    });

    return rows;
  }, [programDocs, windowYearsMeta, isProposedMode, endingYear]);

  /**
   * Total Budget row: sums all program rows column-by-column.
   */
  const totalBudgetRow = useMemo(() => {
    if (!programTableRows.length || !windowYearsMeta.length) return null;

    const row = { label: "Total Budget" };

    windowYearsMeta.forEach(({ colKey }) => {
      const sum = programTableRows.reduce((acc, r) => {
        const v = r[colKey];
        return typeof v === "number" && Number.isFinite(v) ? acc + v : acc;
      }, 0);
      row[colKey] = sum;
    });

    attachIncreaseFields(row, windowYearsMeta);
    return row;
  }, [programTableRows, windowYearsMeta]);

  /**
   * Optionally bubble the computed table rows up to the parent.
   */
  useEffect(() => {
    if (typeof setBoeData !== "function") return;
    if (!programTableRows.length) return;

    setBoeData((prev) => ({
      ...prev,
      programBudgetByYear: {
        description: "Program-level budget totals by year",
        rows: programTableRows,
      },
    }));
  }, [programTableRows, setBoeData]);

  // ---------- Render ----------------------------------------------------

  if (loading) {
    return (
      <div className="p-4 text-sm text-gray-500">
        Loading program-level budget tables…
      </div>
    );
  }

  const has4ColWindow =
    endingYear &&
    windowYearsMeta.length === 4 &&
    tableColumns.length > 0 &&
    programTableRows.length > 0;

  if (!has4ColWindow) {
    return (
      <div className="p-4 text-sm text-gray-500">
        Program budget data is not available for the selected window.
      </div>
    );
  }

  return (
    <TableSearchScope>
    <div className="space-y-4">
      {/* Collapsible header for the whole section */}
      {/* Collapsible header for the whole section */}
      <HeaderToggleButton
        isOpen={showProgramsTable}
        onToggle={() => setShowProgramsTable((prev) => !prev)}
        title="Program Budget by Year"
        ariaLabel="Toggle program budget by year section"
        controlsId="program-budget-by-year-section"
        rightSlot={
          endingYearOptions.length > 0 ? (
            <div
              className="ml-auto flex flex-wrap items-center justify-end gap-2 pr-2 pointer-events-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Excel export button (unstyled icon) */}
              <button
                type="button"
                className="inline-flex items-center justify-center bg-transparent border-0 p-0 m-0 text-gray-700 hover:text-gray-900 focus:outline-none leading-none"
                style={{ lineHeight: 1 }}
                title="Download table to Excel"
                aria-label="Download table to Excel"
                onClick={(e) => {
                  e.stopPropagation();
                  excelExportRef.current?.exportToExcel?.();
                }}
              >
                <span className="relative top-[1px] text-base">💾</span>
              </button>
                {showProgramsTable && <SectionTableSearch />}

              <span className="shrink-0 whitespace-nowrap font-semibold text-xs text-gray-700">
                Ending Year
              </span>
              
              <select
                className="border rounded px-2 py-1 text-xs bg-white"
                value={endingYear || ""}
                onChange={(e) =>
                  setEndingYear(
                    e.target.value ? Number(e.target.value) : endingYear,
                  )
                }
              >
                {endingYearOptions.map((y) => (
                  <option key={y} value={y}>
                    {formatFiscalYearFromStart(y)}
                  </option>
                ))}
              </select>
            </div>
          ) : null
        }
      />

      {/* Everything below is actually collapsible now */}
      {showProgramsTable && (
        <div id="program-budget-by-year-section" className="space-y-2">
          {/* PROGRAMS table */}
          <div className="space-y-2">
            <CommonTable
              toolbarHeading="PROGRAMS"
              excelExportRef={excelExportRef}
              excelFilename={`program-budget-by-year-${endingYear || "latest"}.xlsx`}
              excelSheetName="Programs"
              excelTitle="Program Budget by Year"
              title={null}
              columns={tableColumns}
              data={
                totalBudgetRow
                  ? [...programTableRows, totalBudgetRow]
                  : programTableRows
              }
              isEditing={false}
              headerColor="bg-[#e5eef9]"
              showAggregateTotal={false}
              defaultSortField={null}
              defaultSortOrder="asc"
            />

            {/* PROGRAM TOTALS CHARTS */}
            <div className="space-y-6">
              {/* Approved Budget Chart */}
              {approvedTotalsChart.labels.length > 0 &&
                approvedTotalsChart.series.length > 0 && (
                  <div className="space-y-1">
                    <HeaderToggleButton
                      isOpen={showApprovedTotalsChart}
                      onToggle={() =>
                        setShowApprovedTotalsChart((prev) => !prev)
                      }
                      title="Approved Budget"
                      ariaLabel="Toggle approved totals chart"
                      controlsId="program-totals-approved-chart"
                    />

                    {showApprovedTotalsChart && (
                      <div id="program-totals-approved-chart">
                        <p className="text-xs text-gray-600 mb-1">
                          This chart sums all programs and shows the total
                          approved budget by fiscal year.
                        </p>

                        <LineChartWithToggles
                          labels={approvedTotalsChart.labels}
                          series={approvedTotalsChart.series}
                          showTotalWhenNoneSelected={true}
                          totalLabel="Total Approved Budget"
                          showSeriesToggles={true}
                          proposedRenderMode="merge"
                          showChartTypeSelector={true}
                        />
                      </div>
                    )}
                  </div>
                )}

              {/* Actual Spending Chart */}
              {actualTotalsChart.labels.length > 0 &&
                actualTotalsChart.series.length > 0 && (
                  <div className="space-y-1">
                    <HeaderToggleButton
                      isOpen={showActualTotalsChart}
                      onToggle={() => setShowActualTotalsChart((prev) => !prev)}
                      title="Actual Spending"
                      ariaLabel="Toggle actual totals chart"
                      controlsId="program-totals-actual-chart"
                    />

                    {showActualTotalsChart && (
                      <div id="program-totals-actual-chart">
                        <p className="text-xs text-gray-600 mb-1">
                          This chart sums all programs and shows the total
                          actual spending by fiscal year.
                        </p>

                        <LineChartWithToggles
                          labels={actualTotalsChart.labels}
                          series={actualTotalsChart.series}
                          showTotalWhenNoneSelected={true}
                          totalLabel="Total Actual Spending"
                          showSeriesToggles={true}
                          showChartTypeSelector={true}
                        />
                      </div>
                    )}
                  </div>
                )}

              {/* Actual Spending vs Approved Budget Chart */}
              {diffTotalsChart.labels.length > 0 &&
                diffTotalsChart.series.length > 0 && (
                  <div className="space-y-1">
                    <HeaderToggleButton
                      isOpen={showDiffTotalsChart}
                      onToggle={() => setShowDiffTotalsChart((prev) => !prev)}
                      title="Actual Spending vs Approved Budget"
                      ariaLabel="Toggle actual vs approved totals chart"
                      controlsId="program-totals-diff-chart"
                    />

                    {showDiffTotalsChart && (
                      <div id="program-totals-diff-chart">
                        <p className="text-xs text-gray-600 mb-1">
                          This chart compares total actual spending to the total
                          approved budget for the years where both are
                          available.
                        </p>

                        <LineChartWithToggles
                          labels={diffTotalsChart.labels}
                          series={diffTotalsChart.series}
                          groups={diffTotalsChart.groups}
                        />
                      </div>
                    )}
                  </div>
                )}

              {/* Budget vs Inflation Chart */}
              {inflationLabels.length > 0 &&
                (approvedInflationSeries.length > 0 ||
                  actualInflationSeries.length > 0) && (
                  <div className="space-y-1">
                    <HeaderToggleButton
                      isOpen={showInflationTotalsChart}
                      onToggle={() =>
                        setShowInflationTotalsChart((prev) => !prev)
                      }
                      title="Budget vs Inflation"
                      ariaLabel="Toggle budget vs inflation totals chart"
                      controlsId="program-totals-inflation-chart"
                    />

                    {showInflationTotalsChart && (
                      <div id="program-totals-inflation-chart">
                        <p className="text-xs text-gray-600 mb-1">
                          This chart compares total BoE program budget to an
                          inflation baseline.
                        </p>

                        <BudgetInflationChart
                          labels={inflationLabels}
                          approvedData={approvedInflationSeries}
                          actualData={actualInflationSeries}
                          inflationData={inflationBaselineSeries}
                          inflationDataForActual={
                            inflationBaselineSeriesForActual
                          }
                        />
                      </div>
                    )}
                  </div>
                )}

              {/* Program Share Pie Chart */}
              {pieYearOptions.length > 0 && programSharePieItems.length > 0 && (
                <div className="space-y-1">
                  <HeaderToggleButton
                    isOpen={showProgramSharePieChart}
                    onToggle={() =>
                      setShowProgramSharePieChart((prev) => !prev)
                    }
                    title="Breakdown by Program"
                    ariaLabel="Toggle program share pie chart"
                    controlsId="program-share-pie-chart"
                  />

                  {showProgramSharePieChart && (
                    <div id="program-share-pie-chart">
                      <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
                        <p className="text-xs text-gray-600">
                          This pie shows each program’s share of the selected
                          year’s{" "}
                          <span className="font-semibold">{pieBudgetType}</span>{" "}
                          total budget.
                        </p>

                        <div className="flex items-center gap-4 flex-wrap">
                          {/* Year selector */}
                          <div className="flex items-center gap-2 text-xs">
                            <span className="font-semibold">Year</span>
                            <select
                              className="border rounded px-2 py-1 text-xs"
                              value={pieYear || ""}
                              onChange={(e) =>
                                setPieYear(
                                  e.target.value
                                    ? Number(e.target.value)
                                    : pieYear,
                                )
                              }
                            >
                              {pieYearOptions.map((y) => (
                                <option key={y} value={y}>
                                  {formatFiscalYearFromStart(y)}
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Budget type selector */}
                          <div className="flex items-center gap-2 text-xs">
                            <span className="font-semibold">Type</span>

                            {availablePieTypes.map((t) => (
                              <label
                                key={t}
                                className="flex items-center gap-1 cursor-pointer select-none"
                              >
                                <input
                                  type="radio"
                                  name="pieBudgetType"
                                  value={t}
                                  checked={pieBudgetType === t}
                                  onChange={() => setPieBudgetType(t)}
                                />
                                <span>{t}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      </div>

                      <PieChartWithToggles
                        title={null}
                        items={programSharePieItems}
                        percentOf="all"
                        showLegend={false}
                        showSeriesToggles={false}
                        showChartTypeSelector={false}
                        aggregateOthers={false}
                        includeZeroValues={true}
                        showLegendTable={true}
                        legendTablePlacement="right"
                        legendTableMaxHeight="420px"
                        enableHoverPop={true}
                        hoverPopOffset={12}
                        sliceOrder="valueDesc"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
    </TableSearchScope>
  );
}
